/**
 * Module configuration for the application.
 * Consumes modules_entities.config.json and provides typed module/entity access.
 */

import React from 'react';
import type { LucideIcon } from 'lucide-react';
import modulesEntitiesConfig from '../../shared-config/modules_entities.config.json';
import { getIcon, getIconComponent } from './iconMap';

// Types derived from the JSON config
export interface NavItem {
  labelKey: string;
  descriptionKey?: string;
  path: string;
  icon: React.ReactNode;
  /**
   * The owning module's PERMISSION names — the backend module keys
   * (`ai_agents`, `user_management`, `marketing`), which are not always the
   * module's UI `id` (`ai`, `admin`). Stamped at transform time from the config
   * JSON's own key so `requiresModuleAdmin` can be resolved without the caller
   * carrying the module around. More than one for a module administered in
   * sub-scopes (master_data's `products` and `contacts`), where the nav item is
   * shown to an admin of ANY of them. See {@link ModuleConfig.permissionModules}.
   */
  permissionModules: string[];
  /** If set, renders a section header label before this item. Value is an i18n key. */
  section?: string;
  /**
   * Language-independent search aliases for this page in the module picker.
   * Folded into the picker's fuzzy search so the page is findable by an
   * alternative name (e.g. ['estimates'] for a Quotes page). Never displayed,
   * never translated — kept literal so they match regardless of UI language.
   */
  searchSynonyms?: string[];
  /** If true, this nav item is only shown when the user has Google connected */
  requiresGoogleConnection?: boolean;
  /** If true, this nav item is only shown when the user has Microsoft connected */
  requiresMicrosoftConnection?: boolean;
  /** If true, show this nav item even when it matches the module's defaultPath */
  alwaysShowInNav?: boolean;
  /**
   * If true, this item exists ONLY to make its page findable — the module
   * picker and global search index it exactly like any other nav item, and the
   * sidebar never renders a row for it.
   *
   * `navItems` is the single index every "find me a page" surface reads
   * ({@link file://../utils/moduleSearch.ts}), and it is ALSO the sidebar's
   * render list. Before this flag those two jobs could not be separated, so a
   * page that must not occupy a sidebar row — a module's own Settings page,
   * which `docs/design-system/ui-navigation.md` § "Settings vs Setup" forbids
   * as a main nav item; a tab reached from a hub page; a dashboard tile's
   * destination; an importer opened from a list toolbar — could only be
   * declared by EXEMPTING it in `navItemsCoverage.test.ts`, which made it
   * unsearchable too. "Fresh Start" (`/admin/fresh-start`) was one of ~55 pages
   * in that state, and it is the one a user reported.
   *
   * A `searchOnly` item is not a weaker nav item: it carries the same
   * `searchSynonyms`, the same English-alias folding and the same typo
   * tolerance, and it is gated by the same {@link isNavItemVisible} predicate,
   * so it can never offer a page the sidebar would have hidden.
   */
  searchOnly?: boolean;
  /** If true, this nav item is only shown to admin users */
  requiresAdmin?: boolean;
  /**
   * If true, this nav item is only shown to a module admin of its OWNING module
   * — the same three-way test the backend's `require_module_admin` applies
   * (tenant admin OR `{module}.settings.manage` role OR a T11 `admin` access
   * level). Deliberately distinct from `requiresAdmin`, which is tenant-admin
   * only and would therefore hide the page from the per-module admin who owns
   * it. Use this for a page whose reads all sit behind `require_module_admin`.
   */
  requiresModuleAdmin?: boolean;
  /** If true, this nav item is only shown to sysadmin users (sysadmin tenant) */
  requiresSysadmin?: boolean;
  /**
   * If true, this nav item is only shown when the tenant operates as a freight
   * forwarder (persona flag `sells_freight_services` on — Forwarder TMS). The
   * sidebar reads the persona via `usePersona` and hides the item when off.
   */
  requiresForwarderMode?: boolean;
  /**
   * If true, this nav item is only shown when the tenant has MORE THAN ONE
   * company (legal entity). The multi-company "hide-when-single" rule: a page
   * about the relationship BETWEEN the tenant's own entities has no meaning in
   * a single-company tenant, so it should not occupy a sidebar row there.
   * Same gate the `DocumentCompanyField` / `IntercompanyBadge` components apply
   * to themselves — see docs/platform/multi-company-design.md.
   */
  requiresMultiCompany?: boolean;
  /**
   * If set, this nav item is a cross-module link to another module (canonical owner of the data).
   * Value is the source module ID (e.g., "master_data"). The sidebar shows an indicator + tooltip
   * to signal that clicking will navigate out of the current module.
   * Pattern: only for master-data / shared entities that live in another module.
   */
  crossModule?: string;
}

export interface ColorScheme {
  primary: string;
  light?: string;
  dark?: string;
  contrastText?: string;
}

export interface EntityConfig {
  table: string | null;
  description: string;
  displayNameKey: string;
  pluralKey: string;
  creatable?: boolean;
  icon?: React.ReactNode;
  /**
   * Raw icon name string from config, kept beside the rendered `icon` the way
   * `ModuleConfig.iconName` is — `icon` is fixed at `h-4 w-4`, so a surface that
   * needs another size (a 3.5 chip glyph, a full-width page header) resolves the
   * component itself via {@link getEntityIconComponent} instead of hand-picking a
   * lucide import that then drifts from the config.
   */
  iconName?: string;
  /** Parent entity key within the same module (for navigation hierarchy) */
  parent?: string;
  /** If true, this entity cannot exist without its parent (required FK). Default false. */
  parentRequired?: boolean;
}

/**
 * One tenant-settings AREA of a module — a `/admin/module-settings/{key}` page
 * with its own settings form.
 *
 * Most modules own exactly one, which `tenantSettingsPath` names. A module that
 * owns several declares them all, because that single string is what the admin
 * dashboard tile and the sidebar's shield icon both resolve: an area no entry
 * names is reachable by URL only, however complete its page is.
 */
export interface TenantSettingsArea {
  /** The `/admin/module-settings/{key}` URL segment this area is served at. */
  key: string;
  /** Full route for the area. */
  path: string;
  /** i18n key naming the area in the switcher and the page heading. */
  labelKey: string;
}

export interface ModuleSettingsConfig {
  /** If true, module has user-level settings accessible from sidebar */
  hasUserSettings?: boolean;
  /** If true, module has tenant-level settings (requires admin role) */
  hasTenantSettings?: boolean;
  /** If true, module has sysadmin-level settings (requires sysadmin role) */
  hasSysadminSettings?: boolean;
  /** Full-page route for user settings (navigates instead of opening dialog) */
  userSettingsPath?: string;
  /** Route for tenant-level admin settings (shown to admins in module sidebar) */
  tenantSettingsPath?: string;
  /**
   * Every tenant-settings area, declared only by a module that owns more than
   * one. The first entry is the primary and its `path` is `tenantSettingsPath`
   * — the sidebar shield keeps landing there, and the page it lands on carries
   * the switcher that reaches the rest.
   */
  tenantSettingsAreas?: TenantSettingsArea[];
}

/**
 * Maturity badge a PanLuma product module carries in the shell's own registry. Both
 * names here collide with a generated model out of the SOFTWARE FACTORY module
 * (`shared/schemas/software_factory/module.schema.json`), which catalogues the
 * *development* modules PanLuma builds itself: its `ModuleStatus` is the catalog row's
 * lifecycle (`active | archived`) and its `ModuleCategory` is the engineering bucket
 * (`product | platform | integration | infrastructure`). Neither describes a shipped
 * module's maturity or its placement in this navigation config, and no value overlaps.
 */
// typed-surface-ok: shell module-registry maturity, not the software_factory catalog row's status
export type ModuleStatus = 'stable' | 'beta' | 'alpha' | 'hidden';
// typed-surface-ok: shell nav grouping, not the software_factory catalog row's engineering category
export type ModuleCategory = 'master_data' | 'internal' | 'technical' | 'admin';
/**
 * Domain grouping for the home launcher — buckets "Other modules" under kicker
 * section headers (see docs/module-list.md). Orthogonal to {@link ModuleCategory}
 * (which drives the small type badge). Modules with no group fall into a trailing
 * "More" bucket so nothing is ever dropped.
 */
export type ModuleGroup =
  | 'master_data'
  | 'core_operations'
  | 'supply_chain'
  | 'communication'
  | 'ai'
  | 'product_tools'
  | 'admin';

export interface ModuleConfig {
  id: string;
  /**
   * The module's PERMISSION name — the backend module key that
   * `require_module_admin(module)` and `module_access_levels` are resolved
   * with. This is the config JSON's own object key, which differs from the UI
   * `id` for five modules (`ai_agents`/`ai`, `user_management`/`admin`,
   * `file_management`/`files`, `third_party_services`/`integrations`,
   * `design_system`/`design-system`). Derived here so no surface has to keep a
   * hand-rolled id→permission map (the sidebar used to).
   *
   * Usually one name. master_data carries TWO — `products` and `contacts` — one
   * per separately grantable admin sub-scope.
   */
  permissionModules: string[];
  nameKey: string;
  descriptionKey: string;
  icon: React.ReactNode;
  /** Raw icon name string from config (used for canvas favicon rendering) */
  iconName: string;
  color: string;
  basePaths: string[];
  defaultPath: string;
  navItems: NavItem[];
  /**
   * Language-independent search aliases for this module in the module picker.
   * Folded into the picker's fuzzy search so the module is findable by an
   * alternative name (e.g. ['CRM', 'deals'] for pipeline). Never displayed,
   * never translated — kept literal so they match regardless of UI language.
   *
   * They are usually English, but a NON-ASCII entry is legitimate and
   * deliberate in one case: a localized display name a module has been renamed
   * AWAY from. `people` carries 'אנשים' / 'Personas' / '人员' so Cmd+K still
   * finds it by the names users learned before it became משאבי אנוש /
   * Recursos Humanos / 人力资源. Every alias is matched as a literal substring
   * in every locale, so this costs the other languages nothing. See
   * `docs/design-system/ui-i18n.md` § Translation glossary scope.
   */
  searchSynonyms?: string[];
  entities: Record<string, EntityConfig>;
  /** Only show to users with admin role */
  requiresAdmin?: boolean;
  /**
   * Only show to a module admin of THIS module — the same three-way test as
   * `require_module_admin` (see {@link NavItem.requiresModuleAdmin}). Set on a
   * module whose entire authenticated API surface is module-admin-gated, so a
   * plain member is never invited into a dead end. Distinct from
   * `requiresAdmin` on purpose: that flag is tenant-admin/sysadmin only and
   * would hide the module from the per-module admin who owns it.
   */
  requiresModuleAdmin?: boolean;
  /** Only show to users with sysadmin role */
  requiresSysadmin?: boolean;
  /** Only show in development/debug mode */
  devOnly?: boolean;
  /** Internal module (no UI in module picker) */
  internal?: boolean;
  /** Order in which module appears in home page and module picker (lower = first) */
  displayOrder: number;
  /** If true, module appears directly in the module picker grid */
  showInModulePicker: boolean;
  /** Module maturity status: stable (default), beta, or alpha */
  status: ModuleStatus;
  /** Module category (e.g., master_data for shared reference data modules) */
  category?: ModuleCategory;
  /** Domain grouping for the home launcher (buckets "Other modules" under kicker headers) */
  group?: ModuleGroup;
  /** Settings configuration for sidebar */
  settings?: ModuleSettingsConfig;
  /** If true, the secondary nav panel is completely hidden for this module */
  hideSecondaryNav?: boolean;
  /** If true, module is pinned at the bottom of the module picker (utility modules like Files, Mail) */
  pinned?: boolean;
  /** Access model from modules_entities.config.json (e.g. 'rebac', 'module_onoff') */
  accessModel?: string;
}

// Raw entity config type from JSON (before icon transformation)
interface RawEntityConfig {
  table?: string | null;
  description: string;
  displayNameKey: string;
  pluralKey: string;
  creatable?: boolean;
  icon?: string;
  parent?: string;
  parentRequired?: boolean;
}

// Raw config type from JSON
interface RawModuleConfig {
  id: string;
  /**
   * Overrides the derived permission module (this module's own config key) for
   * a module whose backend gate names are neither its key nor its id, and is how
   * a module declares admin SUB-SCOPES: `master_data` lists
   * `["products", "contacts"]` because its catalog settings routes declare
   * `require_module_admin("products")` and its contacts/organizations settings
   * routes `require_module_admin("contacts")`.
   * Guarded by modules/shared/__tests__/test_module_admin_permission_module_names.py,
   * which walks every gate literal in the backend.
   */
  permissionModules?: string[];
  displayNameKey: string;
  descriptionKey: string;
  defaultColorScheme?: { primary: string };
  icon?: string;
  basePaths?: string[];
  defaultPath?: string;
  navItems?: Array<{ labelKey: string; descriptionKey?: string; path: string; icon: string; section?: string; searchSynonyms?: string[]; requiresGoogleConnection?: boolean; requiresMicrosoftConnection?: boolean; alwaysShowInNav?: boolean; searchOnly?: boolean; requiresAdmin?: boolean; requiresModuleAdmin?: boolean; requiresSysadmin?: boolean; requiresForwarderMode?: boolean; requiresMultiCompany?: boolean; crossModule?: string }>;
  searchSynonyms?: string[];
  entities: Record<string, RawEntityConfig>;
  internal?: boolean;
  toggleable?: boolean;
  alwaysAvailable?: boolean;
  requiresAdmin?: boolean;
  requiresModuleAdmin?: boolean;
  requiresSysadmin?: boolean;
  devOnly?: boolean;
  displayOrder?: number;
  showInModulePicker?: boolean;
  /**
   * If false, the module is NOT enabled by default for a new tenant
   * (opt-in only, e.g. software_factory). Consumed backend-side at tenant
   * creation; declared here so the JSON shape stays typed.
   */
  defaultEnabled?: boolean;
  pinned?: boolean;
  status?: ModuleStatus;
  category?: ModuleCategory;
  group?: ModuleGroup;
  settings?: ModuleSettingsConfig;
  hideSecondaryNav?: boolean;
  accessModel?: string;
}

// Transform raw entity config to typed EntityConfig
function transformEntity(rawEntity: RawEntityConfig): EntityConfig {
  return {
    table: rawEntity.table ?? null,
    description: rawEntity.description,
    displayNameKey: rawEntity.displayNameKey,
    pluralKey: rawEntity.pluralKey,
    creatable: rawEntity.creatable,
    icon: rawEntity.icon ? getIcon(rawEntity.icon) : undefined,
    iconName: rawEntity.icon,
    parent: rawEntity.parent,
    parentRequired: rawEntity.parentRequired,
  };
}

// Transform raw JSON config to typed ModuleConfig.
// `permissionModules` defaults to the config JSON's own object key — the backend
// module name the authz layer resolves with, which is not always `rawModule.id` —
// and holds several names for a module administered in sub-scopes.
function transformModule(rawModule: RawModuleConfig, permissionModules: string[]): ModuleConfig {
  const transformedEntities: Record<string, EntityConfig> = {};
  for (const [key, entity] of Object.entries(rawModule.entities)) {
    transformedEntities[key] = transformEntity(entity);
  }

  return {
    id: rawModule.id,
    permissionModules,
    nameKey: rawModule.displayNameKey,
    descriptionKey: rawModule.descriptionKey,
    icon: getIcon(rawModule.icon) ?? <></>,
    iconName: rawModule.icon ?? '',
    color: rawModule.defaultColorScheme?.primary ?? '#616161',
    basePaths: rawModule.basePaths ?? [],
    defaultPath: rawModule.defaultPath ?? '/',
    navItems: (rawModule.navItems ?? []).map(item => ({
      labelKey: item.labelKey,
      descriptionKey: item.descriptionKey,
      path: item.path,
      icon: getIcon(item.icon) ?? <></>,
      permissionModules,
      section: item.section,
      searchSynonyms: item.searchSynonyms,
      requiresGoogleConnection: item.requiresGoogleConnection,
      requiresMicrosoftConnection: item.requiresMicrosoftConnection,
      alwaysShowInNav: item.alwaysShowInNav,
      searchOnly: item.searchOnly,
      requiresAdmin: item.requiresAdmin,
      requiresModuleAdmin: item.requiresModuleAdmin,
      requiresSysadmin: item.requiresSysadmin,
      requiresForwarderMode: item.requiresForwarderMode,
      requiresMultiCompany: item.requiresMultiCompany,
      crossModule: item.crossModule,
    })),
    searchSynonyms: rawModule.searchSynonyms,
    entities: transformedEntities,
    requiresAdmin: rawModule.requiresAdmin,
    requiresModuleAdmin: rawModule.requiresModuleAdmin,
    requiresSysadmin: rawModule.requiresSysadmin,
    devOnly: rawModule.devOnly,
    internal: rawModule.internal,
    displayOrder: rawModule.displayOrder ?? 500,
    showInModulePicker: rawModule.showInModulePicker ?? false,
    status: rawModule.status ?? 'stable',
    category: rawModule.category,
    group: rawModule.group,
    settings: rawModule.settings,
    hideSecondaryNav: rawModule.hideSecondaryNav,
    pinned: rawModule.pinned,
    accessModel: rawModule.accessModel,
  };
}

// Build MODULES array from config, filtering out internal modules for UI purposes
const rawModules = modulesEntitiesConfig.modules as Record<string, RawModuleConfig>;

// Sort by displayOrder (lower = first)
const sortByDisplayOrder = (a: ModuleConfig, b: ModuleConfig) => a.displayOrder - b.displayOrder;

export const MODULES: ModuleConfig[] = Object.entries(rawModules)
  .filter(([, m]) => !m.internal)
  .map(([key, m]) => transformModule(m, m.permissionModules ?? [key]))
  .sort(sortByDisplayOrder);

// All modules including internal ones (for entity lookups)
export const ALL_MODULES: ModuleConfig[] = Object.entries(rawModules)
  .map(([key, m]) => transformModule(m, m.permissionModules ?? [key]))
  .sort(sortByDisplayOrder);

/** Modules that should appear directly in the module picker grid (excluding pinned) */
export const MODULE_PICKER_MODULES: ModuleConfig[] = MODULES.filter(m => m.showInModulePicker && !m.pinned);

/** Utility modules pinned at the bottom of the module picker */
export const PINNED_MODULES: ModuleConfig[] = MODULES.filter(m => m.showInModulePicker && m.pinned);

/**
 * Order in which domain groups render in the home launcher. A sentinel `'more'`
 * bucket collects any module whose `group` is unset so nothing is ever dropped.
 * Labels come from i18n (`modules.group.{id}`) — the id order is fixed here.
 */
export const MODULE_GROUP_ORDER = [
  'master_data',
  'core_operations',
  'supply_chain',
  'communication',
  'ai',
  'product_tools',
  'admin',
  'more',
] as const;
export type ModuleGroupKey = (typeof MODULE_GROUP_ORDER)[number];

/** Bucket a module into its launcher group key (falls back to the `more` sentinel). */
export function moduleGroupKey(module: ModuleConfig): ModuleGroupKey {
  return module.group ?? 'more';
}

/**
 * Split modules into their domain groups, preserving each group's incoming
 * order and dropping empty groups. Used by the home launcher to render the
 * "Other modules" section under kicker headers.
 */
export function groupModulesByDomain(
  modules: ModuleConfig[]
): Array<{ key: ModuleGroupKey; modules: ModuleConfig[] }> {
  const byKey = new Map<ModuleGroupKey, ModuleConfig[]>();
  for (const m of modules) {
    const key = moduleGroupKey(m);
    const bucket = byKey.get(key);
    if (bucket) bucket.push(m);
    else byKey.set(key, [m]);
  }
  return MODULE_GROUP_ORDER
    .filter((key) => byKey.has(key))
    .map((key) => ({ key, modules: byKey.get(key)! }));
}

/**
 * Module IDs that can be enabled/disabled via settings (not role-based).
 * Hidden modules (status:hidden) are excluded — they must not appear in any
 * module picker (Settings, onboarding, home). They remain valid as a tenant's
 * enabled_modules (server-side validation is broader) so existing tenants and
 * sysadmins aren't broken; they're simply not offered in the picker.
 */
export const TOGGLEABLE_MODULE_IDS = Object.entries(rawModules)
  .filter(([, m]) => m.toggleable && m.status !== 'hidden')
  .map(([, m]) => m.id) as string[];
export type ToggleableModuleId = (typeof TOGGLEABLE_MODULE_IDS)[number];

/** Module IDs that are always available (cannot be disabled) */
export const ALWAYS_AVAILABLE_MODULE_IDS = Object.entries(rawModules)
  .filter(([, m]) => m.alwaysAvailable)
  .map(([, m]) => m.id) as string[];

/**
 * The permission-module names that mean "admin of this module".
 *
 * `module_admin_roles` (from `/me`) carries `Permission.module` — the prefix of a
 * `{name}.settings.manage` code — which is the module's config KEY, not its `id`
 * (`ai_agents`, not `ai`), and for master_data is neither: its products routes gate
 * on `products`. Comparing a config id against that list directly is why a
 * "Products Admin" never saw the settings link they hold the grant for.
 *
 * Single-sourced from `modules_entities.config.json` — the same `permissionModules`
 * field `transformModule` and the backend `get_permission_module_names` read, so no
 * third spelling of this fact can drift.
 *
 * Several names means the module is administered in SUB-SCOPES (master_data's
 * `products` and `contacts`). This list is the module-WIDE question — "admin of
 * anything in it" — which is what the sidebar and module picker ask. A surface
 * gated on one sub-scope asks for that name instead.
 */
export function getPermissionModuleNames(moduleId: string): string[] {
  const entry = Object.entries(rawModules).find(([key, m]) => (m.id ?? key) === moduleId);
  if (!entry) return [moduleId];
  const [moduleKey, moduleConfig] = entry;
  return moduleConfig.permissionModules ?? [moduleKey];
}

/**
 * Whether `name` is a declared permission-module name of some module — i.e. a
 * grantable admin scope in its own right, rather than a module id or folder key.
 *
 * The distinction is what makes sub-scopes work: `useIsModuleAdmin('master_data')`
 * asks the module-wide question (either scope admits), while
 * `useIsModuleAdmin('contacts')` asks for that scope alone, and a "Products Admin"
 * must NOT pass it.
 */
export function isDeclaredPermissionModuleName(name: string): boolean {
  return Object.values(rawModules).some(m => (m.permissionModules ?? []).includes(name));
}

/**
 * The inverse of {@link getPermissionModuleNames}: a permission-module name → the
 * module config `id` that `module_access_levels` is keyed by.
 *
 * Mirrors the backend `get_module_id_for_permission_module_name`. An ordinary name
 * that is already a config key or id passes through to its id, so only the
 * genuinely divergent ones (`products` → `master_data`) need a declaration.
 */
export function getModuleIdForPermissionModuleName(permissionModuleName: string): string {
  for (const [moduleKey, moduleConfig] of Object.entries(rawModules)) {
    if ((moduleConfig.permissionModules ?? []).includes(permissionModuleName)) {
      return moduleConfig.id ?? moduleKey;
    }
  }
  const byKey = rawModules[permissionModuleName];
  return byKey ? (byKey.id ?? permissionModuleName) : permissionModuleName;
}

/** Module IDs that depend on user role, not settings */
export const ROLE_BASED_MODULE_IDS = Object.entries(rawModules)
  .filter(([, m]) => m.requiresAdmin || m.requiresSysadmin || m.devOnly)
  .map(([, m]) => m.id) as string[];

// Module color definitions (extracted from config for quick access)
export const MODULE_COLORS: Record<string, string> = Object.fromEntries(
  Object.values(rawModules)
    .filter(m => m.defaultColorScheme?.primary)
    .map(m => [m.id, m.defaultColorScheme!.primary])
);

/**
 * Mapping from module config keys (Python folder names like "ai_agents", "website_hosting")
 * to their URL base paths (like "ai-agents", "websites"). Used by getEntityUrl() to resolve
 * entity refs with multi-word module prefixes.
 * Sorted by key length descending so longer prefixes match first.
 */
export const MODULE_KEY_TO_BASE_PATH: Array<[string, string]> = Object.entries(rawModules)
  .map(([key, m]) => [key, (m.basePaths?.[0] ?? m.defaultPath ?? `/${key}`).replace(/^\//, '')] as [string, string])
  .sort((a, b) => b[0].length - a[0].length);

/**
 * Find the module that matches a given path
 */
export function getModuleByPath(pathname: string): ModuleConfig {
  // Exact match for home
  if (pathname === '/') {
    return MODULES.find(m => m.id === 'home')!;
  }

  // Find module by matching basePaths
  for (const module of MODULES) {
    if (module.id === 'home') continue; // Skip home for prefix matching
    for (const basePath of module.basePaths) {
      if (pathname.startsWith(basePath)) {
        return module;
      }
    }
  }

  // Default to home if no match
  return MODULES.find(m => m.id === 'home')!;
}

/**
 * Resolve where an invalid / unmatched route should redirect to.
 *
 * If the path's leading segments match a known module's `basePaths`, returns
 * that module's `defaultPath` (its root) so the user lands somewhere relevant.
 * Otherwise returns `'/'` (the app root). Unlike {@link getModuleByPath}, this
 * never silently falls back to the home module — a non-match returns `'/'`
 * explicitly, which is what the global 404 handler wants.
 */
export function getRedirectPathForUnmatched(pathname: string): string {
  if (pathname === '/' || pathname === '') {
    return '/';
  }
  for (const module of MODULES) {
    if (module.id === 'home') continue;
    for (const basePath of module.basePaths) {
      // Match the full base segment only (avoid `/sale` matching `/sales`).
      if (pathname === basePath || pathname.startsWith(`${basePath}/`)) {
        return module.defaultPath || basePath;
      }
    }
  }
  return '/';
}

/**
 * Get a module by its ID
 */
export function getModuleById(id: string): ModuleConfig | undefined {
  return ALL_MODULES.find(m => m.id === id);
}

/**
 * Every tenant-settings area a module owns, in declaration order.
 *
 * The registry is the single source for all of them: the admin dashboard tiles,
 * the sidebar shield and the settings page's own area switcher resolve through
 * here rather than spelling an `/admin/module-settings/...` literal — which is
 * how master_data's contacts area (cadence policy, relationship classes, record
 * visibility, export/import) came to be reachable by URL only.
 *
 * Returns the DECLARED areas, so it is empty for the common module that owns at
 * most the one `tenantSettingsPath` names — the callers that need that single
 * path read it directly, and the switcher is exactly the surface that should
 * render nothing there.
 */
export function getTenantSettingsAreas(moduleId: string): TenantSettingsArea[] {
  return getModuleById(moduleId)?.settings?.tenantSettingsAreas ?? [];
}

/**
 * The route of ONE of a module's declared settings areas, for an in-page pointer
 * at a sibling surface. `undefined` when the module declares no such area — the
 * caller withholds the affordance rather than rendering a dead click.
 */
/**
 * The tenant-settings route this principal may actually OPEN for `moduleId`, or
 * `null` when none.
 *
 * For the ordinary module — one admin scope, one settings page — this is just
 * `tenantSettingsPath` gated on that scope. It earns its keep on a module
 * administered in SUB-SCOPES: master_data's `tenantSettingsPath` points at the
 * catalog area, so gating the sidebar shield on the module-wide question and then
 * sending everyone there would hand a "Contacts & Organizations Admin" a link that
 * `ModuleAdminRoute` bounces them off. Each declared area's `key` IS its scope's
 * permission-module name (`products`, `contacts`), so the first area this user
 * administers is both the right question and the right destination.
 *
 * `isModuleAdmin` is the caller's `useModuleAdminCheck()` — passed in rather than
 * called here, because this file is not a React module and the check is a hook.
 */
export function getAccessibleTenantSettingsPath(
  moduleId: string,
  isModuleAdmin: (permissionModule: string) => boolean,
): string | null {
  const areas = getTenantSettingsAreas(moduleId);
  if (areas.length > 0) {
    const permitted = areas.find(area => isModuleAdmin(area.key));
    if (permitted) return permitted.path;
    // Fall through: an area whose key is not a grantable scope is still covered
    // by the module-wide question below, which the T11 umbrella also satisfies.
  }
  const path = getModuleById(moduleId)?.settings?.tenantSettingsPath;
  if (!path) return null;
  return getPermissionModuleNames(moduleId).some(isModuleAdmin) ? path : null;
}

export function getTenantSettingsAreaPath(
  moduleId: string,
  areaKey: string,
): string | undefined {
  return getTenantSettingsAreas(moduleId).find(area => area.key === areaKey)?.path;
}

/**
 * Module IDs whose bare root path (`basePaths[0]`) intentionally renders a page
 * directly in the router, even though their `defaultPath` is a deeper subpath.
 * These roots must NOT get a module-root redirect (doing so would double-declare
 * the path, or shadow a bare-root page that is distinct from the default page —
 * e.g. `/ai-agents` shows the agents overview, `/brokerage` the brokerage
 * dashboard, `/shipments` the shipments overview). Kept as an explicit allowlist
 * so a newly-added module with a subpath default can't silently start 404ing on
 * its bare root without a deliberate choice here (enforced by the route test).
 */
export const DIRECT_RENDER_ROOT_MODULE_IDS: ReadonlySet<string> = new Set([
  'tasks', // /tasks -> index Navigate to dashboard
  'contracts', // /contracts -> index renders dashboard
  'ai', // /ai-agents -> agents overview page (distinct from dashboard)
  'support', // /support -> dashboard
  'shipments', // /shipments -> shipments overview (distinct from dashboard)
  'inventory', // /inventory -> dashboard
  'procurement', // /procurement -> dashboard
  'sales', // /sales -> dashboard
  'brokerage', // /brokerage -> dashboard (distinct from offer-sheets default)
  'billing', // /billing -> accounts list (the default page)
  'chatbot', // /chatbot -> dashboard
]);

/**
 * Does a module's default page live at a deeper subpath than its bare root?
 * (`/pipeline` root vs `/pipeline/dashboard` default). Internal modules with no
 * base path, and modules whose default already IS the root, return false.
 */
export function hasSubpathDefault(module: ModuleConfig): boolean {
  const root = module.basePaths[0];
  if (!root) return false;
  return module.defaultPath !== root && module.defaultPath.startsWith(`${root}/`);
}

/**
 * Modules whose bare root path has no route of its own and would otherwise fall
 * through to the smart 404. Each bare root should redirect straight to the
 * module's configured `defaultPath` so first-party navigation never flashes the
 * "Page not found → redirecting…" page. Derived from config (targets stay in
 * sync with `defaultPath`) as every subpath-default module NOT in
 * {@link DIRECT_RENDER_ROOT_MODULE_IDS}. Consumed by `App.tsx` route wiring.
 */
export const MODULE_ROOT_REDIRECTS: ReadonlyArray<{ from: string; to: string }> =
  ALL_MODULES.filter(
    m => hasSubpathDefault(m) && !DIRECT_RENDER_ROOT_MODULE_IDS.has(m.id)
  ).map(m => ({ from: m.basePaths[0], to: m.defaultPath }));

/**
 * Get modules filtered by user permissions (role-based only, not settings-based)
 *
 * `isModuleAdmin` resolves the module-admin gate for a given PERMISSION module
 * name — pass the predicate from `useModuleAdminCheck()` so it runs the same
 * three-way test as the backend's `require_module_admin`. It is required, not
 * optional, so a new caller cannot silently skip the gate.
 *
 * Note the deliberate asymmetry with `requiresAdmin`: a sysadmin satisfies that
 * flag (a sysadmin must be able to reach admin modules), whereas
 * `requiresModuleAdmin` has no such fallback — `isModuleAdmin` already admits a
 * tenant admin as one of its own three arms, and a sysadmin without the grant
 * has no business being offered a tenant's module-admin surface.
 */
export function getAccessibleModules(
  isAdmin: boolean,
  isSysadmin: boolean,
  isModuleAdmin: (permissionModule: string) => boolean,
  showDevModules: boolean = false
): ModuleConfig[] {
  return MODULES.filter(module => {
    if (module.internal) return false;
    if (module.status === 'hidden') return false;
    if (module.requiresSysadmin && !isSysadmin) return false;
    if (module.requiresAdmin && !isAdmin && !isSysadmin) return false;
    if (module.requiresModuleAdmin && !module.permissionModules.some(isModuleAdmin)) return false;
    if (module.devOnly && !showDevModules) return false;
    return true;
  });
}

/**
 * Compute effective enabled modules for a user based on platform, tenant, and user settings.
 * Returns modules that are enabled at all levels (intersection).
 *
 * Cascade: platform → tenant → user
 * - NULL at any level means "inherit all from level above"
 * - Explicit array means "only these modules (intersected with above)"
 */
export function getEffectiveEnabledModules(
  platformAvailableModules: string[] | null | undefined,
  tenantEnabledModules: string[] | null | undefined,
  userEnabledModules: string[] | null | undefined,
  moduleCeiling?: string[] | null | undefined
): string[] | null {
  // Start with platform level
  let effective: string[] | null = platformAvailableModules ?? null;

  // Intersect with tenant level
  if (tenantEnabledModules !== null && tenantEnabledModules !== undefined) {
    if (effective === null) {
      effective = tenantEnabledModules;
    } else {
      effective = effective.filter(m => tenantEnabledModules.includes(m));
    }
  }

  // Intersect with module ceiling (set by admin/inviter)
  if (moduleCeiling !== null && moduleCeiling !== undefined) {
    if (effective === null) {
      effective = moduleCeiling;
    } else {
      effective = effective.filter(m => moduleCeiling.includes(m));
    }
  }

  // Intersect with user level
  if (userEnabledModules !== null && userEnabledModules !== undefined) {
    if (effective === null) {
      effective = userEnabledModules;
    } else {
      effective = effective.filter(m => userEnabledModules.includes(m));
    }
  }

  return effective;
}

/**
 * Check if a specific toggleable module is enabled for the user.
 * Role-based modules (admin, sysadmin) are not checked here.
 */
export function isModuleEnabled(
  moduleId: string,
  effectiveEnabledModules: string[] | null
): boolean {
  // Always-available modules
  if (ALWAYS_AVAILABLE_MODULE_IDS.includes(moduleId)) {
    return true;
  }
  // Role-based modules are always "enabled" from settings perspective
  if (ROLE_BASED_MODULE_IDS.includes(moduleId)) {
    return true;
  }
  // If no restrictions, all toggleable modules are enabled
  if (effectiveEnabledModules === null) {
    return true;
  }
  // Check if module is in the enabled list
  return effectiveEnabledModules.includes(moduleId);
}

// Entity lookup utilities

/**
 * Get entity config by table name (searches all modules)
 */
export function getEntityByTable(tableName: string): { module: ModuleConfig; entity: EntityConfig; entityKey: string } | undefined {
  for (const module of ALL_MODULES) {
    for (const [entityKey, entity] of Object.entries(module.entities)) {
      if (entity.table === tableName) {
        return { module, entity, entityKey };
      }
    }
  }
  return undefined;
}

/**
 * The lucide component for an entity's CANONICAL icon, resolved from the `icon`
 * name it declares in `modules_entities.config.json`.
 *
 * Returns the component (not a rendered element) so a caller can size it — which
 * is the whole point: `EntityConfig.icon` is pinned at `h-4 w-4`, so every
 * surface needing a 3.5 chip glyph used to hand-pick its own lucide import, and
 * a product category then rendered as a `Tag` on the inquiry review grid while
 * Master Data rendered it as `Shapes` (WI bbb2fcf4). Prefer the
 * `<EntityIcon table="…">` wrapper at a JSX call site.
 */
export function getEntityIconComponent(tableName: string): LucideIcon | undefined {
  return getIconComponent(getEntityByTable(tableName)?.entity.iconName);
}

/**
 * Find an entity whose table name ends with `_${suffix}`.
 * Used when MCP API path yields a bare resource like "workspaces"
 * but the entity table is "tasks_workspaces".
 */
export function getEntityByTableSuffix(suffix: string): { module: ModuleConfig; entity: EntityConfig; entityKey: string } | undefined {
  const target = `_${suffix}`;
  for (const module of ALL_MODULES) {
    for (const [entityKey, entity] of Object.entries(module.entities)) {
      if (entity.table?.endsWith(target)) {
        return { module, entity, entityKey };
      }
    }
  }
  return undefined;
}

/**
 * Get all entities that are creatable (for quick-create menu)
 */
export function getCreatableEntities(): Array<{ module: ModuleConfig; entity: EntityConfig; entityKey: string }> {
  const result: Array<{ module: ModuleConfig; entity: EntityConfig; entityKey: string }> = [];
  for (const module of MODULES) {
    for (const [entityKey, entity] of Object.entries(module.entities)) {
      if (entity.creatable) {
        result.push({ module, entity, entityKey });
      }
    }
  }
  return result;
}

/**
 * Get creatable entities for a specific module (for module-specific quick-create menu)
 */
export function getCreatableEntitiesForModule(moduleId: string): Array<{ entity: EntityConfig; entityKey: string }> {
  const module = MODULES.find(m => m.id === moduleId);
  if (!module) return [];

  const result: Array<{ entity: EntityConfig; entityKey: string }> = [];
  for (const [entityKey, entity] of Object.entries(module.entities)) {
    if (entity.creatable) {
      result.push({ entity, entityKey });
    }
  }
  return result;
}

/**
 * Get all entity types with their display names (for audit log filters, etc.)
 */
export function getAllEntityTypes(): Array<{ table: string; displayNameKey: string; moduleId: string }> {
  const result: Array<{ table: string; displayNameKey: string; moduleId: string }> = [];
  for (const module of ALL_MODULES) {
    for (const entity of Object.values(module.entities)) {
      if (entity.table) {
        result.push({
          table: entity.table,
          displayNameKey: entity.displayNameKey,
          moduleId: module.id,
        });
      }
    }
  }
  return result;
}

/**
 * Get modules that have at least one entity with a table (for module filter pickers)
 */
export function getModulesWithEntities(): Array<{ id: string; nameKey: string }> {
  return ALL_MODULES
    .filter(m => Object.values(m.entities).some(e => e.table))
    .map(m => ({ id: m.id, nameKey: m.nameKey }));
}

// Must match VERSIONED_ENTITY_TYPES in shared/versioning/__init__.py
const VERSIONED_TABLES = new Set([
  'pipeline_deals',
  'contacts_contacts',
  'contacts_organizations',
  'pipeline_pipelines',
  'recruiting_candidates',
  'recruiting_job_postings',
  'recruiting_applications',
  'recruiting_pipelines',
  'people_employees',
  'accounting_journal_entries',
  'accounting_accounts',
  'accounting_fiscal_periods',
  'items',
  'boards',
  'products',
  'product_categories',
  'product_price_lists',
  'support_tickets',
  'support_kb_articles',
]);

export interface VersionedEntityType {
  table: string;
  displayNameKey: string;
  moduleId: string;
  icon: React.ReactNode | undefined;
}

export interface ModuleWithVersionedEntities {
  module: ModuleConfig;
  entities: VersionedEntityType[];
}

/**
 * Get modules that have versioned entities, with their entity list
 */
export function getModulesWithVersionedEntities(): ModuleWithVersionedEntities[] {
  const result: ModuleWithVersionedEntities[] = [];

  for (const module of ALL_MODULES) {
    const versionedEntities: VersionedEntityType[] = [];

    for (const entity of Object.values(module.entities)) {
      if (entity.table && VERSIONED_TABLES.has(entity.table)) {
        versionedEntities.push({
          table: entity.table,
          displayNameKey: entity.displayNameKey,
          moduleId: module.id,
          icon: entity.icon,
        });
      }
    }

    if (versionedEntities.length > 0) {
      result.push({ module, entities: versionedEntities });
    }
  }

  return result.sort((a, b) => a.module.displayOrder - b.module.displayOrder);
}

/**
 * Get versioned entity types with their display names and icons (for version history filters)
 */
export function getVersionedEntityTypes(): VersionedEntityType[] {
  const result: VersionedEntityType[] = [];

  for (const module of ALL_MODULES) {
    for (const entity of Object.values(module.entities)) {
      if (entity.table && VERSIONED_TABLES.has(entity.table)) {
        result.push({
          table: entity.table,
          displayNameKey: entity.displayNameKey,
          moduleId: module.id,
          icon: entity.icon,
        });
      }
    }
  }
  return result;
}
